import { useMemo, useState } from "react";
import {
  useGetSuppliersQuery,
  useDeleteSupplierMutation,
} from "../../../../redux/api";
import type { Supplier } from "../../../../redux/api";
import {
  IconPlus,
  IconSearch,
  IconEye,
  IconEdit,
  IconTrash,
} from "../../../../components/icons";
import { SupplierFormModal } from "./supplier";
import { EntityOverview } from "../../../../components/EntityOverview";
import { OverviewFieldList } from "../../../../components/OverviewFieldList";
import { statusTone as overviewStatusTone } from "../../../../utils/status";
import { RowMenu } from "../../../../components/RowMenu";

const COLUMN_COUNT = 6;

const statusTone = (status?: string | null) =>
  (status ?? "").toLowerCase() === "active" ? "badge--success" : "badge--warning";

// Inventory → Suppliers (list page).
const Suppliers = () => {
  const {
    data: suppliers = [],
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetSuppliersQuery();

  const [deleteSupplier] = useDeleteSupplierMutation();

  const [search, setSearch] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const openDetail = (id: string | null) => {
    setEditingId(id);
    setModalOpen(true);
  };

  const [viewingId, setViewingId] = useState<string | null>(null);
  const viewing = suppliers.find((s) => s.id === viewingId) ?? null;


  const handleDelete = async (supplier: Supplier) => {
    if (!window.confirm(`Delete "${supplier.name}"?`)) return;
    try {
      await deleteSupplier(supplier.id).unwrap();
    } catch (err) {
      window.alert(
        (err as { message?: string })?.message ?? "Failed to delete supplier.",
      );
    }
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return suppliers;
    return suppliers.filter((s: Supplier) =>
      [s.name, s.code, s.email, s.phone, s.status].some((field) =>
        (field ?? "").toLowerCase().includes(q),
      ),
    );
  }, [suppliers, search]);

  const errorMessage = error
    ? (error as { message?: string }).message ?? "Failed to load suppliers"
    : null;

  if (viewing) {
    return (
      <>
        <EntityOverview
          backLabel="Suppliers"
          onBack={() => setViewingId(null)}
          title={viewing.name}
          id={viewing.id}
          status={{ label: viewing.status, tone: overviewStatusTone(viewing.status) }}
          onEdit={() => openDetail(viewing.id)}
          onDelete={() => {
            void handleDelete(viewing).then(() => setViewingId(null));
          }}
        >
          <OverviewFieldList
            fields={[
              { label: "Code", value: viewing.code },
              { label: "Email", value: viewing.email },
              { label: "Phone", value: viewing.phone },
              { label: "GSTIN", value: viewing.gstin },
              { label: "Contact person", value: viewing.contactPerson },
              { label: "Address line 1", value: viewing.addressLine1, full: true },
              { label: "Address line 2", value: viewing.addressLine2, full: true },
              { label: "City", value: viewing.city },
              { label: "State", value: viewing.state },
              { label: "Country", value: viewing.country },
              { label: "Postal code", value: viewing.postalCode },
              { label: "Lead time (days)", value: viewing.leadTimeDays },
            ]}
          />
        </EntityOverview>
        <SupplierFormModal open={modalOpen} id={editingId} onClose={() => setModalOpen(false)} />
      </>
    );
  }

  return (
    <div className="dashboard">
      <section className="panel">
        <div className="panel__head">
          <div>
            <h2>Suppliers</h2>
            <p className="panel__sub">
              Showing {visible.length} of {suppliers.length}
            </p>
          </div>
          <div className="toolbar">
            <div className="search">
              <span className="search__icon">
                <IconSearch />
              </span>
              <input
                type="search"
                placeholder="Search suppliers…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button
              type="button"
              className="btn"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              {isFetching ? "Refreshing…" : "Refresh"}
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => openDetail(null)}
            >
              <IconPlus /> New supplier
            </button>
          </div>
        </div>

        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Code</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    Loading suppliers…
                  </td>
                </tr>
              ) : errorMessage ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    <div className="table-error">
                      <span>{errorMessage}</span>
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => refetch()}
                      >
                        Try again
                      </button>
                    </div>
                  </td>
                </tr>
              ) : visible.length === 0 ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    {suppliers.length === 0
                      ? "No suppliers yet."
                      : "No suppliers match your search."}
                  </td>
                </tr>
              ) : (
                visible.map((supplier: Supplier) => (
                  <tr key={supplier.id}>
                    <td className="cell-title">
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => setViewingId(supplier.id)}
                      >
                        {supplier.name}
                      </button>
                    </td>
                    <td>
                      {supplier.code ? (
                        <span className="pill pill--neutral">{supplier.code}</span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="cell-date">{supplier.email ?? "—"}</td>
                    <td>{supplier.phone ?? "—"}</td>
                    <td>
                      <span className={`badge ${statusTone(supplier.status)}`}>
                        <span className="badge__dot" />
                        {supplier.status}
                      </span>
                    </td>
                    <td>
                      <RowMenu
                        items={[
                          {
                            label: "View",
                            icon: <IconEye size={16} />,
                            onSelect: () => setViewingId(supplier.id),
                          },
                          {
                            label: "Edit",
                            icon: <IconEdit size={16} />,
                            onSelect: () => openDetail(supplier.id),
                          },
                          {
                            label: "Delete",
                            icon: <IconTrash size={16} />,
                            onSelect: () => handleDelete(supplier),
                            danger: true,
                          },
                        ]}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>


      <SupplierFormModal open={modalOpen} id={editingId} onClose={() => setModalOpen(false)} />
    </div>
  );
};

export default Suppliers;
